// src/forms/data_hub_html_element.js
import { default_theme } from '../helpers/static_vars';
import {
    create_form_elements_and_inputs
} from './html_element_helpers/form_generators';
import {
    watch_container_size,
    generate_action_body
} from './html_element_helpers/form_functions';
import {
    bind_all_tel_inputs_intl,
    set_listener_for_new_user_data,
    resolve_sms_optin_label_text,
    inject_sms_optin_into_user_fields,
    SMS_OPTIN_LABEL_DEFAULT_TEXT,
} from '../tel/telephone_sms_inputs';
import {
    inject_data_hub_css_link,
    inject_html_element_css,
    inject_html_element_utilities_css,
    assign_theming_variables,
    update_original_height,
    get_tailwind_size_class,
    apply_form_chrome_classes
} from './html_element_helpers/form_styling';
import {
    show_error_popover
} from './html_element_helpers/error_popover';
import { inject_loading_skeleton } from './html_element_helpers/loading_skeleton';
import {
    parse_and_validate_form_fields,
    fields_json_contains_type,
    apply_template_to_form_fields
} from './html_element_helpers/custom_fields/fields_schema';
import { log_console_error_and_track_to_action_endpoint_with_rate_limit } from './html_element_helpers/error_tracking';
import { normalize_refcodes_from_object, getDefaultAbAmountsString } from '../helpers/actblue_integration';
import {
    inject_styles,
    get_most_parent_url_shadow_dom_safe,
    parse_custom_html,
    string_array_of_ints_to_array,
    processHandlebarsTemplate,
    parse_b64_or_array_like_data_attribute,
    normalize_metadata_from_object,
    remove_empty_values_from_object
} from '../helpers';
import defaultsDeep from 'lodash.defaultsdeep';
import { poll_with_retry } from '../helpers';
import { extract_array_values_from_url_parameter } from '../helpers/parse_url_params';
import { whitelist_server_person_object_using_outgoing_field_list } from '../helpers/get_process_user_data';
import { v4 as uuidv4 } from 'uuid';
import merge from 'lodash.merge';
import { merge_user_data_browser } from '../helpers/user_data_smart_merge';
import { data_hub_form } from './data_hub_forms';

class DataHubSignupForm extends HTMLElement {
    constructor() {
        super();
        this.attachShadow({ mode: 'open' });
        this.isInitialized = false; // Prevent double rendering
        this.observers = new Set();
        // Internal-only per-element uuid. NEVER logged or sent to the backend.
        // Used for: (a) DOM container id when no explicit data-hub-form-id is provided,
        // (b) rate-limit bucket key so two form elements with the same explicit form_id
        // (e.g. both data-hub-form-id="signup-form") get separate quotas.
        this.element_uuid = uuidv4();
        // Public form_id. Stays null unless org explicitly sets data-hub-form-id.
        // Only this value is sent to the backend (form_metadata.form_id, error logs, etc.) —
        // we don't pollute logs with random client-generated uuids.
        this.final_form_id = null;
    }

    async connectedCallback() {
        if (this.hasAttribute('data-hub-signup-form-initialized')) {
            return;
        }
        // Set the guard attribute synchronously, BEFORE any await, so a second
        // connectedCallback (re-mount, etc) is rejected even if it lands while the
        // first one is still resolving the DataHub instance asynchronously.
        this.setAttribute('data-hub-signup-form-initialized', 'true');

        inject_loading_skeleton(this.shadowRoot);

        await this.initForm().catch(err => {
            console.error('Error initializing data-hub-signup-form:', err);
        })
        .finally(() => {
            this.originalHeight = update_original_height(this.shadowRoot);
        });
    }

    disconnectedCallback() {
        this.observers.forEach(observer => observer.disconnect());
    }

    async initForm() {
        this.isInitialized = true;

        // Set early so it sets listener before data_hub user_data returns
        set_listener_for_new_user_data();

        // Race guard (belt-and-suspenders alongside the deferred registration in
        // main.js): registering this element upgrades already-parsed instances
        // synchronously, which can fire connectedCallback -> initForm a tick
        // before window.DataHub is assigned (deferred / end-of-body bundle). If
        // neither the instance nor the constructor object is present yet, wait
        // briefly for one to appear instead of erroring immediately. The poll's
        // first check yields, letting the bundle finish assigning the global.
        if (!window.data_hub && !(window.DataHub && window.DataHub.data_hub_class)) {
            try {
                await poll_with_retry(
                    () => !!window.data_hub || !!(window.DataHub && window.DataHub.data_hub_class),
                    { interval: 20, timeout: 3000 }
                );
            } catch (_e) {
                // Fall through to the not-found handling below (visible error).
            }
        }

        // Check if window.data_hub exists (the actual instance)
        if (window.data_hub) {
            // DataHub instance already exists, use it directly
            this.data_hub_instance = window.data_hub;
            await this.renderForm();
        } else if (window.DataHub && window.DataHub.data_hub_class) {
            // DataHub constructor exists but no instance yet
            let isLoading = false;
            try {
                // Check if it's the main.js DataHub object (has data_hub_loading property)
                if (window.DataHub.hasOwnProperty('data_hub_loading')) {
                    isLoading = window.DataHub.data_hub_loading;
                } else if (window.data_hub) {
                    // A live instance appeared (e.g. another form constructed it) —
                    // use it rather than bailing.
                    this.data_hub_instance = window.data_hub;
                    await this.renderForm();
                    return;
                } else {
                    // window.DataHub is present but not the bootstrap object AND no
                    // instance exists. Surface a visible error instead of silently
                    // leaving the loading skeleton up forever.
                    this.handleError?.(
                        'window.DataHub is not the expected bootstrap object and no data_hub instance exists — cannot initialize form.',
                        null
                    );
                    return;
                }
            } catch (error) {
                this.handleError?.('Error reading data_hub_loading in initForm: ' + error);
            }
            
            // If a DataHub instance is currently loading...
            if (isLoading) {
                try {
                    await window.DataHub.wait_for_data_hub();
                    this.data_hub_instance = window.data_hub;
                } catch (err) {
                    this.handleError?.('data_hub load timed out, attempting render anyway: ' + err);
                    if (window.data_hub) this.data_hub_instance = window.data_hub;
                }
                await this.renderForm();
            } else {
                // No instance exists and none is loading; trigger creation.
                window.DataHub.data_hub_loading = true;
                await this.initNewDataHub();
            }
        } else {
            // Surface this rather than silently leaving the loading skeleton up forever.
            this.handleError?.(
                'window.DataHub not found — bundle did not load or registered under a different global. Cannot initialize form.',
                null
            );
            const error_container = document.createElement('div');
            error_container.style.cssText = 'padding:24px;text-align:center;font-family:system-ui,sans-serif;color:#6b7280;';
            error_container.innerHTML = '<p style="font-size:14px;">Unable to load form — contact Data Hub administrator.</p>';
            this.shadowRoot.innerHTML = '';
            this.shadowRoot.appendChild(error_container);
        }
        return;
    }

    async initNewDataHub() {
        const initRefcodes = normalize_refcodes_from_object(
            parse_b64_or_array_like_data_attribute(this.dataset.hubRefcodes || this.dataset.hubActblueRefcodes) || {}
        ) || {};
        const initPageMetadata = normalize_metadata_from_object(
            parse_b64_or_array_like_data_attribute(this.dataset.hubPageMetadata || this.dataset.hubMetadata) || {}
        ) || {};
        
        const organization_id_from_attribute =
            this.getAttribute('data-hub-organization-id')
            || this.dataset.hubOrganizationId
            || null;

        const action_domain_from_attribute =
            this.getAttribute('data-hub-action-domain')
            || this.dataset.hubActionDomain
            || null;

        const privacy_policy_from_attribute =
            this.getAttribute('data-hub-privacy-policy')
            || this.dataset.hubPrivacyPolicy
            || null;

        // Passed through user_data merges highest-priority source: data-hub-initial-user-data > url params >
        // parent LS > iframe LS > cookie. B64, fallback key:value
        const initial_user_data_from_attribute = whitelist_server_person_object_using_outgoing_field_list(
            parse_b64_or_array_like_data_attribute(this.dataset.hubInitialUserData) || {}
        );
        const web_analytics_from_attribute =
            this.getAttribute('data-hub-web-analytics')
            || this.dataset.hubWebAnalytics
            || 'all';
        const web_settings_from_attribute = web_analytics_from_attribute === 'none'
            ? { enabled: false }
            : { enabled: true };

        this.data_hub_instance = new window.DataHub.data_hub_class(
            {
                error_function: this.handleError.bind(this),
                actblue_token: this.dataset.abToken || null,
                refcodes: initRefcodes,
                page_metadata: initPageMetadata,
                organization_id: organization_id_from_attribute,
                data_hub_action_domain: action_domain_from_attribute,
                privacy_policy: privacy_policy_from_attribute,
                user_data: initial_user_data_from_attribute,
                web_settings: web_settings_from_attribute,
                data_hub_css_url: this.dataset.hubCssUrl || null,
                inject_data_hub_css: false,
            },
            async (instance) => {
                window.data_hub = instance;
                // Don't overwrite window.DataHub - keep the original object
                // Just set the loading flag if it exists
                if (window.DataHub && typeof window.DataHub.data_hub_loading !== 'undefined') {
                    window.DataHub.data_hub_loading = false;
                }
                this.data_hub_instance = instance;
                try {
                    await this.renderForm();
                } catch (render_error) {
                    // Surface via handleError instead of an unhandled promise rejection.
                    this.handleError?.(
                        'Error during renderForm after DataHub init: ' + (render_error?.stack || render_error?.message || render_error),
                        null
                    );
                }
            }
        );
    }

    async handleError(internalMsg = null, externalMsg = 'Error processing form data.') {
        // Show popover on shadow root (parent error_function shows on document)
        show_error_popover(externalMsg, this.shadowRoot);
        log_console_error_and_track_to_action_endpoint_with_rate_limit({
            internal_error_message: internalMsg,
            external_error_message: externalMsg,
            data_hub_instance: this.data_hub_instance,
            metadata: this.stored_merged_metadata || this.data_hub_instance?.page_metadata || {},
            custom_error_data: {
                source: 'data_hub_signup_form_custom_element',
                // Only attach form_id when org explicitly set data-hub-form-id.
                // Don't pollute backend logs with auto-generated uuids.
                ...(this.final_form_id ? { form_id: this.final_form_id } : {})
            },
            // Internal-only key for the per-form rate-limit bucket. Never sent to backend.
            rate_limit_bucket_key: this.element_uuid,
            maximum_error_tracking_requests_per_page: 3
        });

        const errorEvent = new CustomEvent('data-hub-error', {
            detail: { internal_error_message: internalMsg, external_error_message: externalMsg },
            bubbles: true,
            composed: true
        });
        this.dispatchEvent(errorEvent);
    }

    async renderForm() {

        // Set as rendered
        this.setAttribute('data-hub-signup-form-initialized', 'true');

        // If no organization_id resolved after init, render an error state instead of a broken form.
        // This catches the "shared Cloud Run domain + no org_id" case with a clear message.
        if (!this.data_hub_instance?.organization_id) {
            this.handleError?.(
                'No organization_id resolved — cannot render form. Add data-hub-organization-id attribute or pass organization_id in config.',
                null // no user-facing popover — we render the error inline below
            );
            const error_container = document.createElement('div');
            error_container.style.cssText = 'padding:24px;text-align:center;font-family:system-ui,sans-serif;color:#6b7280;';
            error_container.innerHTML = '<p style="font-size:14px;">Unable to load form — missing organization configuration.</p>';
            this.shadowRoot.innerHTML = '';
            this.shadowRoot.appendChild(error_container);
            return;
        }

        // Use provided form ID. Stays null when not explicitly set so we never log a uuid.
        this.final_form_id = this.dataset.hubFormId || null;

        // Determine actblue token.
        this.actblue_token = this.dataset.abToken || this.data_hub_instance?.actblue_token || null;

        // Shared page_metadata.
        this.page_metadata = normalize_metadata_from_object(parse_b64_or_array_like_data_attribute(this.dataset.hubPageMetadata) || {}) || {};

        // Form specific metadata.
        this.form_metadata = normalize_metadata_from_object(
            parse_b64_or_array_like_data_attribute(this.dataset.hubFormMetadata || this.dataset.hubMetadata) || {}
        ) || {};
        // Only stamp form_id when explicitly set via data-hub-form-id attribute.
        if (this.final_form_id) {
            this.form_metadata.form_id = this.final_form_id;          // legacy key, kept
            this.form_metadata.data_hub_form_id = this.final_form_id; // analytics grouping key
        }

        // Parse actblue refcodes.
        this.refcodes = normalize_refcodes_from_object(
            parse_b64_or_array_like_data_attribute(this.dataset.hubRefcodes || this.dataset.hubActblueRefcodes) || {}
        ) || {};

        if (!this.data_hub_instance) {
            this.handleError?.('data_hub_instance is not available in renderForm');
            return;
        }
        
        // If has this.page_metadata, defaultDeep merge it into data_hub_instance.page_metadata
        if (this.page_metadata) {
            if (!this.data_hub_instance.page_metadata || typeof this.data_hub_instance.page_metadata !== 'object') {
                this.data_hub_instance.page_metadata = {};
            }
            this.data_hub_instance.page_metadata = defaultsDeep(
                {},
                this.data_hub_instance.page_metadata,
                this.page_metadata
            );
        }

        // If has refcodes, add any new ones into data_hub_instance.page_metadata.refcodes
        if (this.refcodes) {
            if (typeof this.data_hub_instance.page_metadata.refcodes === 'object') {
                this.data_hub_instance.page_metadata.refcodes = defaultsDeep(
                    this.data_hub_instance.page_metadata.refcodes,
                    this.refcodes
                );
            } else {
                this.data_hub_instance.page_metadata.refcodes = this.refcodes;
            }
        }

        // Set up the error function.
        this.error_function = this.handleError.bind(this);

        // Setup on success callback based on data-hub-return-function-name
        const returnFunctionName = this.dataset.hubReturnFunctionName;
        // Default return function that just logs the returns
        this.success_js_function = (returns) => {};

        // If a function name was provided, try to get the actual function
        if (returnFunctionName && typeof returnFunctionName === 'string') {
            if (typeof window[returnFunctionName] === 'function') {
                // Get the function from window object
                const userFunction = window[returnFunctionName];
                
                // Check if the function has the expected number of parameters
                if (userFunction.length < 1) {
                    console.warn(`Warning: ${returnFunctionName} has fewer than 1 parameters. Expected: (returns)`);
                }
                
                // Assign the function, ensuring it's bound to the window context
                this.success_js_function = function(returns) {
                    return userFunction.call(window, returns);
                };
            } else {
                this.error_function?.(`Error: passed return function name "${returnFunctionName}" not found on window object.`);
            }
        }

        // Build the Handlebars context (instance user_data wins over globals; complexKeys-safe).
        let current_user_data_context = await merge_user_data_browser({}, window?.data_hub_user_data || {});
        current_user_data_context = await merge_user_data_browser(
            current_user_data_context,
            window?.DataHub?.user_data || {}
        );
        current_user_data_context = await merge_user_data_browser(
            current_user_data_context,
            this.data_hub_instance?.user_data || {}
        );

        // Expose page/form/organization metadata separately so templates can address each
        // explicitly. Keep `metadata` as the merged view for backwards compatibility with
        // existing templates that already reference {{metadata.foo}}.
        const this_organization_metadata = this.data_hub_instance?.organization_metadata
            || (typeof window !== 'undefined' ? window.data_hub_organization_metadata : null)
            || {};
        this.handlebars_context = {
            user_data: current_user_data_context,
            metadata: merge({}, this.page_metadata, this.form_metadata),
            page_metadata: this.page_metadata || {},
            form_metadata: this.form_metadata || {},
            organization_metadata: this_organization_metadata
        };

        // Process template strings.
        this.form_title = processHandlebarsTemplate(this.dataset.hubFormTitle, this.handlebars_context) || null;
        this.form_description = processHandlebarsTemplate(this.dataset.hubFormDescription, this.handlebars_context) || null;
        this.image_url = processHandlebarsTemplate(this.dataset.hubImageUrl, this.handlebars_context) || null;
        this.form_submit_button_text = processHandlebarsTemplate(this.dataset.hubFormSubmitButtonText, this.handlebars_context) || 'Sign up';
        this.user_fields = this.dataset.hubFormUserFields || 'email:3/4,submit:1/4';

        // Rich field layout (data-hub-form-fields, base64 UTF-8 JSON). Strict validation:
        // any violation reports via error_function and returns null, so the legacy
        // data-hub-form-user-fields path above renders as the fallback.
        this.form_fields_rows = null;
        if (this.dataset.hubFormFields) {
            this.form_fields_rows = parse_and_validate_form_fields(this.dataset.hubFormFields, this.error_function);
            if (this.form_fields_rows) {
                // Handlebars pre-pass over authored strings (text/label/placeholder/value)
                // for parity with legacy title/description templating.
                apply_template_to_form_fields(
                    this.form_fields_rows,
                    (str) => processHandlebarsTemplate(str, this.handlebars_context)
                );
            }
        }

        this.form_border = this.dataset.hubFormBorder || 'true';
        // Structured form-chrome attributes. Each is a fixed enum validated by
        // resolve_form_chrome_classes — invalid values fire error_function and
        // fall back to the master border default. See FORM_CHROME_ENUMS in
        // form_styling.js for the allowed values.
        this.form_chrome_options = {
            border: this.form_border,
            border_color: this.dataset.hubFormBorderColor || null,
            shadow: this.dataset.hubFormShadow || null,
            rounded: this.dataset.hubFormRounded || null,
            bg: this.dataset.hubFormBg || null,
            padding: this.dataset.hubFormPadding || null,
            margin_top: this.dataset.hubFormMarginTop || null,
            margin_bottom: this.dataset.hubFormMarginBottom || null,
        };

        // Auto-enable SMS opt-in when an org sets data-hub-sms-optin-label-text but forgot
        // to add `sms_optin` to data-hub-form-user-fields. Inserts `sms_optin:full` right
        // before submit so the resulting string matches a hand-written equivalent.
        const has_sms_optin_label_text_attr = typeof this.dataset.hubSmsOptinLabelText === 'string'
            && this.dataset.hubSmsOptinLabelText.trim() !== '';
        if (!this.form_fields_rows && has_sms_optin_label_text_attr && !this.user_fields.includes('sms_optin')) {
            this.user_fields = inject_sms_optin_into_user_fields(this.user_fields);
        }

        // Resolve the SMS opt-in label text once. Same string is rendered on the page AND
        // logged to form_metadata so the legal record matches what the user actually saw.
        // Only stamp metadata when sms_optin is part of this form — otherwise we'd pollute
        // every submission's metadata with an unused label.
        this.sms_optin_label_text = null;
        if (
            (!this.form_fields_rows && typeof this.user_fields === 'string' && this.user_fields.includes('sms_optin'))
            || fields_json_contains_type(this.form_fields_rows, 'sms_optin')
        ) {
            const templated_custom_label = processHandlebarsTemplate(
                parse_custom_html(this.dataset.hubSmsOptinLabelText),
                this.handlebars_context
            );
            const templated_default_label = processHandlebarsTemplate(
                SMS_OPTIN_LABEL_DEFAULT_TEXT,
                this.handlebars_context
            );
            this.sms_optin_label_text = resolve_sms_optin_label_text(
                templated_custom_label,
                templated_default_label
            );
            this.form_metadata.sms_optin_label_text = this.sms_optin_label_text;
        }

        // Process custom HTML.
        this.custom_html = null;
        const passed_custom_html = this.dataset.hubFormCustomHtml || this.dataset.hubCustomFormHtml;
        if (passed_custom_html && passed_custom_html !== 'undefined') {
            this.custom_html = processHandlebarsTemplate(parse_custom_html(passed_custom_html), this.handlebars_context);
        }

        this.success_settings = this.dataset.hubOnSuccessShow || getDefaultAbAmountsString();
        this.actblue_donation_amount = isNaN(this.dataset.hubActblueDonationAmount) ? null : this.dataset.hubActblueDonationAmount;

        // Process Iterable IDs.
        this.iterable_subscribe_list_ids = string_array_of_ints_to_array(
            extract_array_values_from_url_parameter(this.dataset.hubIterableSubscribeListIds || []),
            true
        );
        this.iterable_unsubscribe_list_ids = string_array_of_ints_to_array(
            extract_array_values_from_url_parameter(this.dataset.hubIterableUnsubscribeListIds || []),
            true
        );
        this.iterable_unsubscribe_message_types = string_array_of_ints_to_array(
            extract_array_values_from_url_parameter(this.dataset.hubIterableUnsubscribeMessageTypeIds || this.dataset.hubIterableUnsubscribeMessageTypes || []),
            true
        );
        this.iterable_subscribe_message_types = string_array_of_ints_to_array(
            extract_array_values_from_url_parameter(this.dataset.hubIterableSubscribeMessageTypeIds || this.dataset.hubIterableSubscribeMessageTypes || []),
            true
        );
        this.allow_prefill = this.dataset.hubFormAllowPrefill ? (this.dataset.hubFormAllowPrefill === 'true') : true;

        // Theming.
        this.default_theme = default_theme;
        assign_theming_variables(this);

        this.align_form_content = this.dataset.hubFormAlignContent || '';

        // Build the main container.
        const most_parent_div = document.createElement('div');
        most_parent_div.classList.add('data_hub_most_parent_container');

        const data_hub_form_element_container = document.createElement('div');
        data_hub_form_element_container.classList.add(
            'relative', 'data-hub-container', 'container',
            'max-w-full', 'mx-auto', 'mt-0', 'mb-0', 'p-4',
            'transition-all', 'duration-100', 'ease-in-out'
        );

        apply_form_chrome_classes(most_parent_div, this.form_chrome_options, this.error_function);

        data_hub_form_element_container.id = this.final_form_id || this.element_uuid;

        let primary_form_settings = {
            data_hub_form_element_container,
            form_title: this.form_title,
            form_description: this.form_description,
            user_fields: this.user_fields,
            form_fields_rows: this.form_fields_rows,
            error_function: this.error_function,
            allow_prefill: this.allow_prefill,
            form_submit_button_text: this.form_submit_button_text,
            image_url: this.image_url,
            success_settings: this.success_settings,
            actblue_donation_amount: this.actblue_donation_amount,
            refcodes: this.refcodes,
            custom_form_html: this.custom_html,
            page_metadata: this.page_metadata,
            form_metadata: this.form_metadata,
            sms_optin_label_text: this.sms_optin_label_text,
            target: this.shadowRoot,
            data_hub_instance: this.data_hub_instance,
        };

        if (this.align_form_content.includes(':')) {
            const [side, fraction] = this.align_form_content.split(':').map(s => s.trim());
            const textColumnWidthClass = get_tailwind_size_class(fraction);
            const [num, denom] = fraction.split('/').map(Number);
            const remainingFraction = `${denom - num}/${denom}`;
            const formColumnWidthClass = get_tailwind_size_class(remainingFraction);
            primary_form_settings.alignSettings = { side, textColumnWidthClass, formColumnWidthClass };
        }

        const { success_div_container, first_form_container, form, submit_button } =
            create_form_elements_and_inputs(primary_form_settings);
        this.submit_button = submit_button;

        data_hub_form_element_container.appendChild(success_div_container);
        data_hub_form_element_container.appendChild(first_form_container);
        most_parent_div.appendChild(data_hub_form_element_container);

        // The body that will go into iterable custom event.
        // form_id is included only when explicitly set via data-hub-form-id; otherwise omitted
        // so backend logs aren't polluted with random client-generated uuids.
        let action_object_data = {
            "source": "data_hub_form",
            ...(this.final_form_id ? { form_id: this.final_form_id } : {}),
            "form_title": this.form_title,
            "form_description": this.form_description,
            "form_image_url": this.image_url,
            "success_settings": this.success_settings,
            "actblue_donation_amount": this.actblue_donation_amount,
            "metadata": "{{metadata}}",
            "page_url": get_most_parent_url_shadow_dom_safe() || null,
            "iterableCampaignId": this.data_hub_instance?.iterableCampaignId || null,
            "iterableTemplateId": this.data_hub_instance?.iterableTemplateId || null,
            "iterable_subscribe_list_ids": this.iterable_subscribe_list_ids || null,
            "iterable_unsubscribe_list_ids": this.iterable_unsubscribe_list_ids || null,
            "iterable_unsubscribe_message_types": this.iterable_unsubscribe_message_types || null,
            "iterable_subscribe_message_types": this.iterable_subscribe_message_types || null,
        };

        // Remove empties
        action_object_data = remove_empty_values_from_object(action_object_data);

        const context = {
            iterable_subscribe_list_ids: this.iterable_subscribe_list_ids,
            iterable_unsubscribe_list_ids: this.iterable_unsubscribe_list_ids,
            iterable_unsubscribe_message_types: this.iterable_unsubscribe_message_types,
            iterable_subscribe_message_types: this.iterable_subscribe_message_types,
            submit_button: this.submit_button,
            allow_prefill: this.allow_prefill,
            originalHeight: this.originalHeight,
            success_settings: this.success_settings,
            actblue_donation_amount: this.actblue_donation_amount,
            refcodes: this.refcodes,
            actblue_token: this.actblue_token,
            dataset: this.dataset
        };

        const merged_metadata = merge({}, this.page_metadata, this.form_metadata);

        const event_settings = generate_action_body({
            element_context: this,
            context: context,
            this_data_hub_instance: this.data_hub_instance,
            most_parent_div: most_parent_div,
            first_form_container: first_form_container,
            success_div_container: success_div_container,
            success_js_function: this.success_js_function,
            error_function: this.error_function,
            action_object_data: action_object_data,
            merged_metadata: merged_metadata,
            form_metadata: this.form_metadata
        });


        if (!event_settings || Object.keys(event_settings).length === 0) {
            this.error_function?.('event_settings is not loaded or is an empty object');
            return;
        }

        // Store metadata on element instance for error tracking
        this.stored_merged_metadata = merged_metadata;

        // Initialize the DataHub form.
        const this_form_instance = new data_hub_form({
            data_hub_instance: this.data_hub_instance,
            target: most_parent_div,
            error_function: this.error_function,
            return_function: this.return_function,
        });
        this_form_instance.init(event_settings);

        const loading_overlay = document.createElement('div');
        loading_overlay.classList.add(//'fixed', 'top-0', 'left-0', 
            'data-hub-loading-overlay', 'w-full', 'h-full',
            'bg-white', 'bg-opacity-50', 'z-50', 'hidden'
        );
        most_parent_div.appendChild(loading_overlay);

        this.shadowRoot.innerHTML = '';
        // Keep generated utility CSS and theme CSS inside the custom element shadow root.
        // Parent pages can still run DataHub in document context, but the default signup
        // element should not leak Tailwind utility selectors into an organization's site.
        const data_hub_css_url = this.dataset.hubCssUrl
            || this.data_hub_instance?.data_hub_css_url
            || null;
        inject_data_hub_css_link(this.shadowRoot, {
            data_hub_css_url: data_hub_css_url,
            error_function: this.error_function
        });
        inject_html_element_css(this.shadowRoot);
        // Tailwind utilities for the JS-generated form DOM (flex, mb-4, w-full, etc.).
        // Bundled at build time from src/**/*.{html,js} only — keeps the form
        // self-contained when no data_hub_css_url is configured. The org's CSS
        // (if any) loaded above by inject_data_hub_css_link layers on top for
        // org-specific HTML templates.
        inject_html_element_utilities_css(this.shadowRoot);
        inject_styles(this.theme, this.shadowRoot);
        this.shadowRoot.appendChild(most_parent_div);

        // Init all telephone inputs. Await to init and inject css / bind to inputs
        this.iti_inputs = await bind_all_tel_inputs_intl(most_parent_div, this.data_hub_instance);
        this.data_hub_instance.iti_inputs = this.iti_inputs;

        watch_container_size(most_parent_div, this.observers, 601);

    }
}

// Registration is EXPLICIT (main.js calls this AFTER assigning window.DataHub) —
// NOT a top-level import side-effect. Why it matters: main.js imports this module
// before it sets window.DataHub, and as soon as customElements.define() runs the
// browser SYNCHRONOUSLY upgrades every already-connected <data-hub-signup-form>
// and invokes its connectedCallback -> initForm. With a deferred / end-of-body
// bundle the form elements are already parsed, so that upgrade fires a tick
// BEFORE window.DataHub exists, and initForm errored with "DataHub not found".
// Registering after the global is assigned closes the race at the source (and
// initForm also polls for the global as a belt-and-suspenders fallback). Once
// defined, the browser still handles all later-parsed elements automatically.
export function register_data_hub_signup_form_element() {
    if (typeof window !== 'undefined'
        && window.customElements
        && !window.customElements.get('data-hub-signup-form')) {
        window.customElements.define('data-hub-signup-form', DataHubSignupForm);
        window.data_hub_signup_form = DataHubSignupForm;
    }
}
